/**
 * Who an exam is for.
 *
 * Publishing used to publish to EVERYONE: the student dashboard selected
 * `WHERE status != 'draft'` with no reference to the student at all. That was
 * the same thing while the app served one student and wrong the moment it
 * served a cohort — you could not give one group a paper without handing it to
 * everyone, and you could not run two exams side by side.
 *
 * The rule the backend implements (see `0011_exam_assignment.sql`) is short:
 * **an exam with an empty roster is open to every student.** Zero assigned
 * means everybody, not nobody. That is why the migration needed no backfill
 * and why no existing exam silently went invisible — but it is also the one
 * thing an admin can misread with real consequences, so this card never leaves
 * it to be inferred. The two states are named on screen ("Everyone" / "Only
 * these students"), the mode is a radio rather than a side effect of an empty
 * list, and saving "only these" with nothing ticked is refused outright with
 * the reason spelled out, because that request would quietly do the opposite
 * of what it looks like.
 *
 * The other honest thing this card owes the admin is the blast radius of
 * narrowing: students dropped from the roster keep their attempts but stop
 * seeing the exam. Anyone in that group who has already sat it is named in a
 * confirmation step before the save goes out.
 */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Users } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Dialog,
  Input,
  useToast,
} from "@/components/ui";
import type { AdminUser } from "@exam/shared";
import {
  fetchAttempts,
  fetchExamStudents,
  fetchUsers,
  setExamStudents,
} from "../data/client";
import { EmptyState, ErrorNote, Loading } from "./Shell";
import { useAsync } from "../lib/useAsync";

/**
 * The roster's two states, named. `everyone` is stored as an empty list — the
 * mapping is done once, here, so nothing below has to reason about "empty".
 */
type RosterMode = "everyone" | "selected";

export function ExamRosterCard({ examId }: { examId: string }) {
  const roster = useAsync(() => fetchExamStudents(examId), [examId]);
  const students = useAsync(
    // Only active accounts: a deactivated student cannot sign in, so offering
    // them in the picker would promise access the login already refuses.
    // `fetchUsers` pages at 200, which is far above any cohort this app runs;
    // if that ever stops being true the picker silently truncates and this
    // call needs a page loop like `fetchAttempts` has.
    () => fetchUsers({ role: "student", active: true }),
    [],
  );
  // Used only to warn before a narrowing save. It is deliberately not required
  // for the save itself — if this request fails the card still works and says
  // that it could not check.
  const attempts = useAsync(() => fetchAttempts({ exam_id: examId }), [examId]);

  const loading = roster.loading || students.loading;
  const error = roster.error ?? students.error;

  return (
    <Card>
      <CardHeader
        title="Who this exam is for"
        description="An exam with nobody assigned is open to every student — that is the default, not a mistake."
        actions={
          roster.data ? (
            <Badge tone={roster.data.open_to_all ? "accent" : "neutral"}>
              {roster.data.open_to_all
                ? "Everyone"
                : `${roster.data.student_ids.length} assigned`}
            </Badge>
          ) : null
        }
      />

      {loading ? (
        <Loading />
      ) : error ? (
        <CardBody>
          <ErrorNote error={error} />
        </CardBody>
      ) : (
        <RosterEditor
          // Re-seeds the form whenever the saved roster changes underneath it,
          // which is the same trick the settings card uses: the draft belongs
          // to the admin typing into it right up until the saved value moves.
          key={(roster.data?.student_ids ?? []).join(",")}
          examId={examId}
          initialIds={roster.data?.student_ids ?? []}
          students={students.data?.users ?? []}
          attempts={attempts.data?.attempts ?? []}
          attemptsUnavailable={attempts.error !== null}
          attemptsLoading={attempts.loading}
          onSaved={() => roster.reload()}
        />
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Editor                                                              */
/* ------------------------------------------------------------------ */

/** The subset of an attempt row this card needs: who sat, under what name. */
interface AttemptRow {
  attempt: { user_id: string };
  student_name: string;
}

function RosterEditor({
  examId,
  initialIds,
  students,
  attempts,
  attemptsUnavailable,
  attemptsLoading,
  onSaved,
}: {
  examId: string;
  initialIds: string[];
  students: AdminUser[];
  attempts: AttemptRow[];
  attemptsUnavailable: boolean;
  attemptsLoading: boolean;
  onSaved: () => void;
}) {
  const { toast } = useToast();

  const wasOpenToAll = initialIds.length === 0;
  const initialSet = useMemo(() => new Set(initialIds), [initialIds]);

  const [mode, setMode] = useState<RosterMode>(
    wasOpenToAll ? "everyone" : "selected",
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialIds));
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === "") return students;
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(needle) ||
        s.email.toLowerCase().includes(needle),
    );
  }, [students, query]);

  /**
   * Everyone who has already sat this exam, by id. A student can have an
   * attempt without being in the active list — deactivating an account does
   * not delete its record — so this map carries its own names rather than
   * looking them up in `students`.
   */
  const attemptNames = useMemo(() => {
    const acc = new Map<string, string>();
    for (const row of attempts) acc.set(row.attempt.user_id, row.student_name);
    return acc;
  }, [attempts]);

  /**
   * Students who can see this exam today and would not be able to after this
   * save, and who have already started it. Their attempts survive — nothing
   * here deletes anything — but the exam disappears from their dashboard, so
   * they are worth naming before the request goes out rather than after.
   *
   * When the exam is currently open to all, everybody had access, so having an
   * attempt is the only evidence that a particular person is affected in a way
   * anyone will notice. When it is already narrowed, only the named roster
   * counts as "had access".
   */
  const losingAccessWithAttempt = useMemo(() => {
    if (mode === "everyone") return [];
    return [...attemptNames]
      .filter(([id]) => (wasOpenToAll || initialSet.has(id)) && !selected.has(id))
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [mode, attemptNames, wasOpenToAll, initialSet, selected]);

  /** Named students being dropped, whether or not they have sat the exam. */
  const removedCount = useMemo(() => {
    if (mode === "everyone") return 0;
    return initialIds.filter((id) => !selected.has(id)).length;
  }, [mode, initialIds, selected]);

  const dirty =
    mode === "everyone"
      ? !wasOpenToAll
      : wasOpenToAll ||
        selected.size !== initialSet.size ||
        [...selected].some((id) => !initialSet.has(id));

  /**
   * The dangerous save: "only these students" with nothing ticked stores an
   * empty list, and an empty list means EVERYONE. It is refused rather than
   * confirmed, because the admin who reaches this state is trying to restrict
   * the exam and the request would do the exact opposite of that intent.
   */
  const emptySelection = mode === "selected" && selected.size === 0;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setMode(wasOpenToAll ? "everyone" : "selected");
    setSelected(new Set(initialIds));
    setQuery("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const ids = mode === "everyone" ? [] : [...selected];
      const result = await setExamStudents(examId, ids);
      setConfirmOpen(false);
      toast({
        tone: "success",
        title: result.open_to_all
          ? "This exam is open to every student."
          : `This exam is assigned to ${result.student_ids.length} ${
              result.student_ids.length === 1 ? "student" : "students"
            }.`,
      });
      onSaved();
    } catch (err) {
      toast({
        tone: "error",
        title: "Could not save the roster.",
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  /** Save straight through unless somebody with an attempt is losing access. */
  const requestSave = () => {
    if (losingAccessWithAttempt.length > 0) {
      setConfirmOpen(true);
      return;
    }
    void save();
  };

  return (
    <>
      <CardBody className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="sr-only">Who can see this exam</legend>

          <ModeOption
            checked={mode === "everyone"}
            onSelect={() => setMode("everyone")}
            icon={<Users />}
            title="Everyone"
            description="Every active student sees this exam once it is published. This is what an exam with nobody assigned does."
          />
          <ModeOption
            checked={mode === "selected"}
            onSelect={() => setMode("selected")}
            title="Only these students"
            description="Nobody else sees it, and nobody else can start it — assignment gates both the listing and the start."
          />
        </fieldset>

        {mode === "selected" ? (
          students.length === 0 ? (
            <EmptyState>
              There are no active students yet, so there is nobody to assign.{" "}
              <Link to="/admin/students" className="text-accent hover:underline">
                Add a student
              </Link>{" "}
              first, or leave this exam open to everyone.
            </EmptyState>
          ) : (
            <div className="space-y-2">
              <div className="flex items-end justify-between gap-3">
                <Input
                  label="Find a student"
                  placeholder="Name or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-64"
                />
                <div className="flex items-center gap-2 pb-0.5">
                  <span className="tabular text-xs text-ink-muted">
                    {selected.size} of {students.length}{" "}
                    {students.length === 1 ? "student" : "students"}
                  </span>
                  <Button
                    size="sm"
                    disabled={visible.length === 0}
                    onClick={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        for (const s of visible) next.add(s.id);
                        return next;
                      })
                    }
                  >
                    {/* Scoped to the search, so a filtered "select all" cannot
                        quietly rope in students the admin cannot see. */}
                    Select {query.trim() === "" ? "all" : "these"}
                  </Button>
                  <Button
                    size="sm"
                    disabled={selected.size === 0}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="max-h-72 overflow-y-auto rounded-md border border-border">
                {visible.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-ink-faint">
                    No student matches &ldquo;{query.trim()}&rdquo;.
                  </p>
                ) : (
                  <ul>
                    {visible.map((student) => (
                      <li
                        key={student.id}
                        className="border-b border-border last:border-b-0"
                      >
                        <label
                          className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 ${
                            selected.has(student.id) ? "bg-accent-soft" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(student.id)}
                            onChange={() => toggle(student.id)}
                            className="size-4 rounded-xs border-border-strong text-accent focus:ring-accent"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs font-medium text-ink">
                              {student.name}
                            </span>
                            <span className="block truncate text-2xs text-ink-faint">
                              {student.email}
                            </span>
                          </span>
                          {student.attempt_count > 0 ? (
                            <span className="tabular shrink-0 text-2xs text-ink-faint">
                              {student.attempt_count}{" "}
                              {student.attempt_count === 1
                                ? "attempt"
                                : "attempts"}
                            </span>
                          ) : null}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {emptySelection ? (
                <p className="rounded-md border border-violation/25 bg-violation-soft px-3 py-2 text-xs text-violation">
                  Nobody is ticked. Saving this would store an empty roster,
                  which means <strong>every student</strong> — the opposite of
                  restricting the exam. Tick at least one student, or choose
                  &ldquo;Everyone&rdquo; above if that is what you want.
                </p>
              ) : null}
            </div>
          )
        ) : null}

        {dirty && !emptySelection ? (
          <ChangeSummary
            mode={mode}
            wasOpenToAll={wasOpenToAll}
            selectedCount={selected.size}
            removedCount={removedCount}
            losingAccessWithAttempt={losingAccessWithAttempt}
            attemptsLoading={attemptsLoading}
            attemptsUnavailable={attemptsUnavailable}
          />
        ) : null}

        {/*
          There is no "assigned by" or "assigned at" on screen because the
          contract does not carry it: `ExamStudentsResponse` is
          `{ student_ids, open_to_all }` and nothing more, even though the
          `exam_students` table stores both columns. Surfacing it needs the API
          to return them first.
        */}
      </CardBody>

      <CardFooter>
        <span className="mr-auto text-2xs text-ink-faint">
          {dirty ? "Unsaved changes." : "Saved."}
        </span>
        <Button
          icon={<RotateCcw />}
          disabled={busy || !dirty}
          onClick={reset}
        >
          Reset
        </Button>
        <Button
          variant="primary"
          disabled={busy || !dirty || emptySelection}
          onClick={requestSave}
        >
          Save roster
        </Button>
      </CardFooter>

      <Dialog
        busy={busy}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Some of these students have already sat this exam"
        description="Their attempts are kept. The exam just stops appearing for them."
        footer={
          <>
            <Button disabled={busy} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => void save()}
            >
              Save anyway
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="rounded-md border border-review/25 bg-review-soft px-3 py-2 text-xs text-review">
            <span className="tabular">{losingAccessWithAttempt.length}</span>{" "}
            {losingAccessWithAttempt.length === 1 ? "student" : "students"} with
            an attempt on this paper will no longer see it. Nothing they
            submitted is deleted, and their results stay on the attempts and
            analysis screens — but they lose the exam from their dashboard and
            cannot start it again.
          </p>
          <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border bg-surface-sunken px-3 py-2">
            {losingAccessWithAttempt.map((student) => (
              <li key={student.id} className="text-xs text-ink">
                {student.name}
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

/** One half of the radio pair. Whole row is the target, not just the dot. */
function ModeOption({
  checked,
  onSelect,
  title,
  description,
  icon,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  icon?: ReactNode;
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 ${
        checked
          ? "border-accent/25 bg-accent-soft"
          : "border-border bg-surface-sunken"
      }`}
    >
      <input
        type="radio"
        name="exam-roster-mode"
        checked={checked}
        onChange={onSelect}
        className="mt-0.5 size-4 border-border-strong text-accent focus:ring-accent"
      />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-xs font-medium text-ink">
          {icon ? (
            <span className="shrink-0 text-ink-muted [&_svg]:size-3.5">
              {icon}
            </span>
          ) : null}
          {title}
        </span>
        <span className="mt-0.5 block text-2xs text-ink-muted">
          {description}
        </span>
      </span>
    </label>
  );
}

/**
 * What this save will actually change, in words. The roster decides who sits a
 * graded paper, so "12 selected" on its own is not enough — the admin needs to
 * see the direction of the change and who it takes the exam away from.
 */
function ChangeSummary({
  mode,
  wasOpenToAll,
  selectedCount,
  removedCount,
  losingAccessWithAttempt,
  attemptsLoading,
  attemptsUnavailable,
}: {
  mode: RosterMode;
  wasOpenToAll: boolean;
  selectedCount: number;
  removedCount: number;
  losingAccessWithAttempt: { id: string; name: string }[];
  attemptsLoading: boolean;
  attemptsUnavailable: boolean;
}) {
  const narrowing = mode === "selected";

  return (
    <div className="space-y-2">
      <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-ink-muted">
        {mode === "everyone" ? (
          <>
            Saving opens this exam to <strong>every active student</strong>.
            Anyone assigned to it now keeps their access; everyone else gains
            it.
          </>
        ) : wasOpenToAll ? (
          <>
            Saving narrows this exam from everyone to{" "}
            <span className="tabular">{selectedCount}</span>{" "}
            {selectedCount === 1 ? "student" : "students"}. Every other student
            loses it from their dashboard.
          </>
        ) : (
          <>
            Saving leaves <span className="tabular">{selectedCount}</span>{" "}
            {selectedCount === 1 ? "student" : "students"} assigned
            {removedCount > 0 ? (
              <>
                , dropping <span className="tabular">{removedCount}</span> who
                can see it today
              </>
            ) : null}
            .
          </>
        )}
      </p>

      {narrowing && attemptsUnavailable ? (
        <p className="rounded-md border border-review/25 bg-review-soft px-3 py-2 text-xs text-review">
          The attempts for this exam could not be read, so this card cannot say
          whether anyone losing access has already sat it. Their attempts would
          be kept either way — check the attempts screen before saving if that
          matters.
        </p>
      ) : narrowing && attemptsLoading ? (
        <p className="text-2xs text-ink-faint">
          Checking who has already sat this exam…
        </p>
      ) : narrowing && losingAccessWithAttempt.length > 0 ? (
        <p className="rounded-md border border-review/25 bg-review-soft px-3 py-2 text-xs text-review">
          <span className="tabular">{losingAccessWithAttempt.length}</span> of
          them {losingAccessWithAttempt.length === 1 ? "has" : "have"} already
          started this exam. Their attempts are kept, but the exam disappears
          from their dashboard — you will be asked to confirm.
        </p>
      ) : null}
    </div>
  );
}
